/**
 * Values of a punch list item's pick-list fields, and their conversion to and
 * from the item form's controls.
 *
 * Category and type can hold several values. The API carries them as
 * `categories` / `types` (string arrays) and keeps the single `category` /
 * `type` strings as a derived copy, the values joined with ", ", for older
 * app builds, search and the PDF. An API that predates the arrays sends only
 * the single string, which is then the one value.
 *
 * Whether a field takes several values is the project's choice (`multiple`
 * in its punchlist_item_config, see project-punchlist-item-config.ts), and it
 * can change after items were saved. The form control holds an array where
 * the field takes several values and a single string (or null) where it takes
 * one. An item that already holds several values on a single-value field is
 * shown as one option, the values joined the way the API joins them; left
 * alone it is saved back as the same values, and picking another option
 * replaces them with that one value.
 *
 * Pure functions only: nothing here changes the item or the arrays it is
 * given.
 */
export type PunchlistMultiValueField = 'category' | 'type';

export interface PunchlistValueOption {
  value: string;
  label: string;
}

/** How the API joins several values into the single `category` / `type` string. */
export const PUNCHLIST_VALUE_SEPARATOR = ', ';

const ARRAY_KEYS: Record<PunchlistMultiValueField, string> = {
  category: 'categories',
  type: 'types',
};

/** Trimmed, non-empty, each value once: what the API stores. */
const clean = (values: unknown[]): string[] => [
  ...new Set(
    values
      .map(value => (typeof value === 'string' ? value.trim() : ''))
      .filter(value => value !== '')
  ),
];

/**
 * The item's categories or types. The array wins when the item has one, even
 * an empty one; without it the single string is the one value.
 */
export function punchlistItemValues(
  item: Record<string, unknown> | null | undefined,
  field: PunchlistMultiValueField
): string[] {
  const values = item?.[ARRAY_KEYS[field]];
  if (Array.isArray(values)) {
    return clean(values);
  }
  return clean([item?.[field]]);
}

/** The item's categories or types as read-only text: the values joined with ", ". */
export function punchlistItemValuesText(
  item: Record<string, unknown> | null | undefined,
  field: PunchlistMultiValueField
): string {
  return punchlistItemValues(item, field).join(PUNCHLIST_VALUE_SEPARATOR);
}

/**
 * Control value for `values`: the array itself on a field that takes several,
 * otherwise the one value, null for none, and the joined values for an item
 * that holds more than the field now takes.
 */
export function toPunchlistFormValue(values: string[], multiple: boolean): string[] | string | null {
  if (multiple) {
    return [...values];
  }
  if (values.length === 0) {
    return null;
  }
  return values.length === 1 ? values[0] : values.join(PUNCHLIST_VALUE_SEPARATOR);
}

/**
 * The values a control holds, as the API takes them. `stored` are the values
 * the item was opened with: a single-value control still showing them joined
 * means the field was not touched, and all of them are kept.
 */
export function fromPunchlistFormValue(formValue: unknown, stored: string[]): string[] {
  if (Array.isArray(formValue)) {
    return clean(formValue);
  }
  if (stored.length > 1 && formValue === stored.join(PUNCHLIST_VALUE_SEPARATOR)) {
    return [...stored];
  }
  return clean([formValue]);
}

/**
 * Dropdown options: the lookup's values in the order the API returned them
 * (it has already resolved and ordered the list for the project), then
 * whatever the item holds that the list no longer has, so that a stored value
 * is always shown and survives a save. On a single-value field an item that
 * holds several also gets them as one joined option, which is the one
 * selected when the form opens.
 */
export function punchlistValueOptions(
  lookupValues: string[],
  stored: string[],
  multiple: boolean
): PunchlistValueOption[] {
  const values = clean([...lookupValues, ...stored]);
  if (!multiple && stored.length > 1) {
    const joined = stored.join(PUNCHLIST_VALUE_SEPARATOR);
    if (!values.includes(joined)) {
      values.push(joined);
    }
  }
  return values.map(value => ({ value, label: value }));
}
